import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import type { ChapterReportClassSession } from '@/types/chapterReport'

interface SessionDraft {
  note: string
  version: number
  error: string | null
  saved: boolean
}

interface Props {
  learnerName: string
  sessions: ChapterReportClassSession[]
  drafts?: Record<number, SessionDraft>
  editable?: boolean
  savingAttendanceId?: number | null
  onDraftChange?: (attendanceId: number, note: string) => void
  onSave?: (attendanceId: number) => void
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10)
  return new Intl.DateTimeFormat('ko-KR', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  }).format(date)
}

export function SessionCommentEditor({
  learnerName,
  sessions,
  drafts,
  editable = false,
  savingAttendanceId,
  onDraftChange,
  onSave,
}: Props) {
  const { t } = useTranslation('coaching')
  const visibleSessions = editable
    ? sessions
    : sessions.filter((session) => session.coachingNote?.trim())

  if (!editable && visibleSessions.length === 0) return null

  return (
    <section className="space-y-6" data-testid="chapter-report-sessions">
      <h2 className="px-2 text-base font-bold text-[#131313]">
        {t('chapterReport.sessions', { name: learnerName })}
      </h2>
      {visibleSessions.length === 0 ? (
        <div className="rounded-xl bg-[#f5f5f5] px-5 py-6 text-center text-xs text-[#888]">
          {t('chapterReport.commentsEmpty')}
        </div>
      ) : (
        <div>
          {visibleSessions.map((session, index) => {
            const draft = drafts?.[session.attendanceId]
            const note = editable ? (draft?.note ?? session.coachingNote ?? '') : (session.coachingNote ?? '')
            const isSaving = savingAttendanceId === session.attendanceId
            return (
              <article key={session.attendanceId} className="chapter-report-row flex items-stretch">
                <div className="flex w-8 shrink-0 flex-col items-center">
                  <span className={`${index === 0 ? 'bg-transparent' : 'bg-[#eaeaea]'} h-4 w-0.5`} />
                  <span className="size-2.5 rounded-full bg-[#4599ff]" />
                  <span className={`${index === visibleSessions.length - 1 ? 'bg-transparent' : 'bg-[#eaeaea]'} min-h-4 flex-1 w-0.5`} />
                </div>
                <div className="mb-2.5 min-w-0 flex-1 rounded-xl bg-[#f5f5f5] px-4 pb-4 pt-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <strong className="text-xs text-[#4599ff]">{formatDate(session.attendanceDate)}</strong>
                    <div className="flex flex-wrap justify-end gap-x-2 gap-y-1 text-[10px] text-[#888]">
                      {session.completedLearning.length === 0 ? (
                        <span>{t('chapterReport.sessionLearningEmpty')}</span>
                      ) : (
                        session.completedLearning.map((learning) => (
                          <span key={`${learning.kind}-${learning.lessonId}-${learning.completedAt}`}>
                            {learning.chapterTitle && `${learning.chapterTitle} / `}
                            {learning.lessonNumber != null && `${learning.lessonNumber}. `}
                            <strong><ContentMetadataText text={learning.lessonTitle} /></strong>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  {editable ? (
                    <div className="mt-3">
                      <label htmlFor={`session-comment-${session.attendanceId}`} className="sr-only">
                        {t('chapterReport.sessionComment', { date: session.attendanceDate })}
                      </label>
                      <textarea
                        id={`session-comment-${session.attendanceId}`}
                        rows={3}
                        value={note}
                        onChange={(event) => onDraftChange?.(session.attendanceId, event.target.value)}
                        placeholder={t('chapterReport.sessionCommentPlaceholder')}
                        className="w-full resize-y rounded-lg border border-[#d0d0d0] bg-white px-3 py-2 text-sm leading-6 outline-none focus:border-[#4599ff] focus:ring-2 focus:ring-[#4599ff]/20"
                      />
                      <div className="mt-2 flex min-h-8 items-center justify-between gap-3">
                        <p aria-live="polite" className={`text-xs ${draft?.error ? 'text-red-600' : 'text-[#2a9d68]'}`}>
                          {draft?.error || (draft?.saved ? t('chapterReport.commentSaved') : '')}
                        </p>
                        <button
                          type="button"
                          onClick={() => onSave?.(session.attendanceId)}
                          disabled={isSaving || note === (session.coachingNote ?? '')}
                          className="rounded-lg bg-[#4599ff] px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {isSaving ? t('chapterReport.savingComment') : t('chapterReport.saveComment')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 whitespace-pre-wrap text-xs font-medium leading-[1.7] tracking-[-0.6px] text-[#131313]">
                      {note}
                    </p>
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

export type { SessionDraft }
